import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Trash2, BarChart3, Briefcase, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [jobForm, setJobForm] = useState({
    titre: '',
    description: '',
    localisation: '',
    domaine: '',
    type_contrat: 'CDI',
    salaire: '',
    url: ''
  });

  useEffect(() => {
    const companyData = localStorage.getItem('company');
    if (!companyData) {
      navigate('/company/auth');
      return;
    }
    setCompany(JSON.parse(companyData));
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('company_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/company/stats`, { headers }),
        axios.get(`${API}/company/jobs`, { headers })
      ]);

      setStats(statsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('company_token');
      await axios.post(`${API}/company/jobs`, jobForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Offre publiée avec succès!');
      setDialogOpen(false);
      setJobForm({
        titre: '',
        description: '',
        localisation: '',
        domaine: '',
        type_contrat: 'CDI',
        salaire: '',
        url: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) return;

    try {
      const token = localStorage.getItem('company_token');
      await axios.delete(`${API}/company/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Offre supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company');
    navigate('/');
  };

  const getTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch {
      return 'Récemment';
    }
  };

  if (loading && !company) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">{company?.nom}</h1>
                <p className="text-sm text-slate-500">{company?.secteur}</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Offres publiées</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Offres actives</p>
                <p className="text-3xl font-bold text-slate-900">{stats.active_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Vues totales</p>
                <p className="text-3xl font-bold text-slate-900">{stats.views || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Mes offres d'emploi</h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Publier une offre
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Publier une nouvelle offre</DialogTitle>
                  <DialogDescription>
                    Remplissez les informations de votre offre d'emploi
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateJob} className="space-y-4 mt-4">
                  <div>
                    <Label>Titre du poste</Label>
                    <Input
                      value={jobForm.titre}
                      onChange={(e) => setJobForm({ ...jobForm, titre: e.target.value })}
                      required
                      placeholder="Ex: Développeur Full Stack"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Localisation</Label>
                      <Input
                        value={jobForm.localisation}
                        onChange={(e) => setJobForm({ ...jobForm, localisation: e.target.value })}
                        required
                        placeholder="Ex: Kinshasa"
                      />
                    </div>
                    <div>
                      <Label>Domaine</Label>
                      <Input
                        value={jobForm.domaine}
                        onChange={(e) => setJobForm({ ...jobForm, domaine: e.target.value })}
                        required
                        placeholder="Ex: Technologie"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Type de contrat</Label>
                      <Select value={jobForm.type_contrat} onValueChange={(value) => setJobForm({ ...jobForm, type_contrat: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CDI">CDI</SelectItem>
                          <SelectItem value="CDD">CDD</SelectItem>
                          <SelectItem value="Stage">Stage</SelectItem>
                          <SelectItem value="Freelance">Freelance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Salaire (optionnel)</Label>
                      <Input
                        value={jobForm.salaire}
                        onChange={(e) => setJobForm({ ...jobForm, salaire: e.target.value })}
                        placeholder="Ex: 1500-2500 USD"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Description du poste</Label>
                    <Textarea
                      value={jobForm.description}
                      onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                      required
                      rows={6}
                      placeholder="Décrivez le poste, les responsabilités, les qualifications requises..."
                    />
                  </div>
                  <div>
                    <Label>Lien de candidature (optionnel)</Label>
                    <Input
                      value={jobForm.url}
                      onChange={(e) => setJobForm({ ...jobForm, url: e.target.value })}
                      placeholder="https://..."
                      type="url"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={loading}>
                    {loading ? 'Publication...' : 'Publier l\'offre'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Jobs List */}
          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucune offre publiée</p>
              <p className="text-sm text-slate-500 mt-2">Publiez votre première offre pour commencer</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="p-6 border border-slate-200 rounded-lg hover:border-indigo-200 transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-slate-900">{job.titre}</h3>
                        <Badge className="bg-indigo-100 text-indigo-700">{job.domaine}</Badge>
                      </div>
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">{job.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <span>📍 {job.localisation}</span>
                        <span>💼 {job.type_contrat}</span>
                        {job.salaire && <span>💰 {job.salaire}</span>}
                        <span>🕒 {getTimeAgo(job.date_publication)}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteJob(job.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CompanyDashboard;
